import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "button", 
    "selectedText", 
    "arrow", 
    "hiddenInput", 
    "dropdown", 
    "searchInput", 
    "loading", 
    "resultsList", 
    "noResults", 
    "error"
  ]
  static values = { 
    url: String,
    minChars: { type: Number, default: 2 },
    delay: { type: Number, default: 300 }
  }

  connect() {
    console.log('Client dropdown search controller connected')
    this.searchTimeout = null
    this.isOpen = false
    this.setupEventListeners()
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    this.removeEventListeners()
  }

  setupEventListeners() {
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleOutsideClick.bind(this))
    
    // Handle keyboard navigation
    this.searchInputTarget.addEventListener('keydown', this.handleKeydown.bind(this))
  }

  removeEventListeners() {
    document.removeEventListener('click', this.handleOutsideClick.bind(this))
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.closeDropdown()
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeDropdown()
    } else if (event.key === 'Enter') {
      event.preventDefault()
      // Select the first result if available
      const firstResult = this.resultsListTarget.querySelector('[data-action*="selectClient"]')
      if (firstResult) {
        firstResult.click()
      }
    }
  }

  toggleDropdown() {
    console.log('Toggle dropdown called, isOpen:', this.isOpen)
    if (this.isOpen) {
      this.closeDropdown()
    } else {
      this.openDropdown()
    }
  }

  openDropdown() {
    this.isOpen = true
    this.dropdownTarget.classList.remove('hidden')
    this.arrowTarget.style.transform = 'rotate(180deg)'
    
    // Focus the search input
    setTimeout(() => {
      this.searchInputTarget.focus()
    }, 100)
  }

  closeDropdown() {
    this.isOpen = false
    this.dropdownTarget.classList.add('hidden')
    this.arrowTarget.style.transform = 'rotate(0deg)'
    
    // Clear search input
    this.searchInputTarget.value = ''
    this.hideAllStates()
  }

  search() {
    const query = this.searchInputTarget.value.trim()
    
    // Clear any existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    
    // Hide results if query is too short
    if (query.length < this.minCharsValue) {
      this.hideAllStates()
      return
    }
    
    // Debounce the search
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, this.delayValue)
  }

  async performSearch(query) {
    this.showLoading()
    
    const searchUrl = this.urlValue || '/oms/clients/search'
    
    try {
      const csrfToken = document.querySelector('[name="csrf-token"]')?.content
      const response = await fetch(`${searchUrl}?q=${encodeURIComponent(query)}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': csrfToken
        }
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      this.showResults(data.results || data)
    } catch (error) {
      console.error('Client search failed:', error)
      this.showError()
    }
  }

  showLoading() {
    this.hideAllStates()
    this.loadingTarget.classList.remove('hidden')
  }

  showResults(clients) {
    this.hideAllStates()
    
    if (clients.length === 0) {
      this.noResultsTarget.classList.remove('hidden')
    } else {
      this.resultsListTarget.innerHTML = clients.map(client => this.clientResultHtml(client)).join('')
    }
  }

  showError() {
    this.hideAllStates()
    this.errorTarget.classList.remove('hidden')
  }

  hideAllStates() {
    this.loadingTarget.classList.add('hidden')
    this.noResultsTarget.classList.add('hidden')
    this.errorTarget.classList.add('hidden')
    this.resultsListTarget.innerHTML = ''
  }

  selectClient(event) {
    event.preventDefault()
    const button = event.currentTarget
    const clientData = JSON.parse(button.dataset.client)
    
    // Set the hidden field value
    this.hiddenInputTarget.value = clientData.id
    
    // Update the display text
    this.selectedTextTarget.textContent = clientData.name
    
    // Close dropdown
    this.closeDropdown()
    
    // Trigger form submission for auto-filter
    this.hiddenInputTarget.dispatchEvent(new CustomEvent('change'))
  }

  clearSelection() {
    // Clear all fields
    this.hiddenInputTarget.value = ''
    this.selectedTextTarget.innerHTML = '<span class="text-neutral-500">Search by name or email...</span>'
    
    // Trigger form submission for auto-filter
    this.hiddenInputTarget.dispatchEvent(new CustomEvent('change'))
  }

  clientResultHtml(client) {
    return `
      <button type="button"
              class="w-full text-left px-4 py-3 hover:bg-neutral-50 flex items-center justify-between transition-colors"
              data-action="click->oms--client-dropdown-search#selectClient"
              data-client='${JSON.stringify(client).replace(/'/g, '&apos;')}'>
        <div>
          <p class="font-medium text-neutral-900">${this.escapeHtml(client.name)}</p>
          ${client.provider ? `<p class="text-sm text-neutral-600">${this.escapeHtml(this.formatProvider(client.provider))} Account</p>` : ''}
        </div>
        <div class="flex items-center space-x-2">
          <span class="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
            client.status === 'active' 
              ? 'bg-success-100 text-success-700' 
              : 'bg-neutral-100 text-neutral-700'
          }">
            ${client.status}
          </span>
        </div>
      </button>
    `
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  formatProvider(provider) {
    // Convert provider name to title case
    // e.g., 'austraclear' -> 'Austraclear', 'treasury_direct' -> 'Treasury Direct'
    return provider
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }
}
